import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Settings2, TrendingUp, TrendingDown, RefreshCw, 
  AlertTriangle, CheckCircle, ArrowRight, DollarSign,
  Clock, Target, Shield, Zap, Brain, Loader2, X,
  ArrowUpRight, ArrowDownRight, Repeat, Hand, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PositionManager = ({ isOpen, onClose, position, onExecute }) => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedRecommendations, setSelectedRecommendations] = useState(new Set());
  const [positionSummary, setPositionSummary] = useState(null);
  const [marketOutlook, setMarketOutlook] = useState('');
  const [urgency, setUrgency] = useState('MEDIUM');
  const [currentStockPrice, setCurrentStockPrice] = useState(0);

  // Fetch recommendations when modal opens
  useEffect(() => {
    if (isOpen && position) {
      fetchRecommendations();
    }
  }, [isOpen, position]);

  const fetchRecommendations = async () => {
    if (!position) return;
    
    setLoading(true);
    setRecommendations([]);
    setSelectedRecommendations(new Set());
    
    try {
      const response = await axios.post(`${API}/position/manage`, {
        symbol: position.symbol || position.underlying_symbol,
        underlying_symbol: position.underlying_symbol,
        option_type: position.option_type || 'call',
        strike_price: position.strike_price,
        expiration_date: position.expiration_date,
        quantity: position.quantity || 1,
        entry_premium: position.entry_premium || position.average_open_price,
        current_premium: position.current_premium || position.close_price,
        cost_basis: position.cost_basis,
        market_value: position.market_value,
        unrealized_pnl: position.unrealized_gain_loss,
        is_spread: position.is_spread || false,
        strategy_name: position.strategy_name,
        legs: position.legs
      });

      if (response.data.success) {
        setRecommendations(response.data.recommendations || []);
        setPositionSummary(response.data.position_summary);
        setMarketOutlook(response.data.market_outlook);
        setUrgency(response.data.urgency);
        setCurrentStockPrice(response.data.current_stock_price);
        toast.success('AI recommendations loaded');
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const toggleRecommendation = (id) => {
    setSelectedRecommendations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleExecuteSelected = () => {
    const selected = recommendations.filter(r => selectedRecommendations.has(r.id));
    if (selected.length === 0) {
      toast.error('Please select at least one recommendation');
      return;
    }
    
    // For now, show a confirmation toast with the selected actions
    const actions = selected.map(r => r.title).join(', ');
    toast.success(`Selected actions: ${actions}`, {
      description: 'Execute through your brokerage platform',
      duration: 5000
    });
    
    if (onExecute) {
      onExecute(selected);
    }
  };

  // Get action icon
  const getActionIcon = (action) => {
    switch (action) {
      case 'HOLD': return <Hand className="h-5 w-5 text-blue-500" />;
      case 'CLOSE': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'ROLL': return <Repeat className="h-5 w-5 text-purple-500" />;
      case 'ADJUST': return <Settings2 className="h-5 w-5 text-orange-500" />;
      default: return <Target className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get risk level badge
  const getRiskBadge = (risk) => {
    switch (risk) {
      case 'LOW': return <Badge className="bg-green-100 text-green-700">Low Risk</Badge>;
      case 'MEDIUM': return <Badge className="bg-yellow-100 text-yellow-700">Medium Risk</Badge>;
      case 'HIGH': return <Badge className="bg-red-100 text-red-700">High Risk</Badge>;
      default: return <Badge variant="outline">{risk}</Badge>;
    }
  };

  // Get recommender badge
  const getRecommenderBadge = (type) => {
    switch (type) {
      case 'Conservative': return <Badge className="bg-blue-100 text-blue-700">🛡️ Conservative</Badge>;
      case 'Aggressive': return <Badge className="bg-red-100 text-red-700">🔥 Aggressive</Badge>;
      case 'Balanced': return <Badge className="bg-purple-100 text-purple-700">⚖️ Balanced</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  // Get urgency indicator
  const getUrgencyIndicator = () => {
    switch (urgency) {
      case 'HIGH': 
        return (
          <div className="flex items-center text-red-600 animate-pulse">
            <AlertTriangle className="h-5 w-5 mr-1" />
            <span className="font-bold">ACTION NEEDED</span>
          </div>
        );
      case 'MEDIUM':
        return (
          <div className="flex items-center text-yellow-600">
            <Clock className="h-5 w-5 mr-1" />
            <span className="font-medium">Review Soon</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center text-green-600">
            <CheckCircle className="h-5 w-5 mr-1" />
            <span>Position OK</span>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold flex items-center">
                <Brain className="h-6 w-6 mr-2 text-purple-600" />
                AI Position Manager
              </DialogTitle>
              <DialogDescription className="mt-1">
                {position?.underlying_symbol} - Expert recommendations for your position
              </DialogDescription>
            </div>
            <div className="flex items-center space-x-4">
              {getUrgencyIndicator()}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Position Summary */}
            {positionSummary && (
              <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-0">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Position</p>
                      <p className="text-lg font-bold">
                        {positionSummary.direction} {positionSummary.quantity} {positionSummary.option_type}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Strike / Exp</p>
                      <p className="text-lg font-bold">
                        ${positionSummary.strike} | {positionSummary.days_to_expiration} DTE
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Entry → Current</p>
                      <p className="text-lg font-bold">
                        ${positionSummary.entry_premium?.toFixed(2)} → ${positionSummary.current_premium?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">P/L</p>
                      <p className={`text-lg font-bold ${positionSummary.pnl_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {positionSummary.pnl_percent >= 0 ? '+' : ''}{positionSummary.pnl_percent}%
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline">{positionSummary.moneyness}</Badge>
                      <span className="text-sm text-gray-500">Stock @ ${currentStockPrice?.toFixed(2)}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={fetchRecommendations}
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Market Outlook */}
            {marketOutlook && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <TrendingUp className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Market Outlook</p>
                    <p className="text-sm text-amber-700">{marketOutlook}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
                <p className="text-gray-600">AI is analyzing your position...</p>
                <p className="text-sm text-gray-400">Generating expert recommendations</p>
              </div>
            )}

            {/* Recommendations */}
            {!loading && recommendations.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold flex items-center">
                    <Zap className="h-5 w-5 mr-2 text-purple-600" />
                    AI Recommendations
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedRecommendations.size} of {recommendations.length} selected
                  </p>
                </div>

                {recommendations.map((rec, idx) => (
                  <Card 
                    key={rec.id || idx}
                    className={`cursor-pointer transition-all ${
                      selectedRecommendations.has(rec.id) 
                        ? 'ring-2 ring-purple-500 bg-purple-50' 
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleRecommendation(rec.id)}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start space-x-4">
                        {/* Checkbox */}
                        <div className="pt-1">
                          <Checkbox 
                            checked={selectedRecommendations.has(rec.id)}
                            onCheckedChange={() => toggleRecommendation(rec.id)}
                            data-testid={`rec-checkbox-${rec.id}`}
                          />
                        </div>

                        {/* Action Icon */}
                        <div className="flex-shrink-0">
                          {getActionIcon(rec.action)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-3">
                          {/* Header */}
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-bold text-lg">{rec.title}</h4>
                              <Badge className={`${
                                rec.action === 'HOLD' ? 'bg-blue-500' :
                                rec.action === 'CLOSE' ? 'bg-red-500' :
                                rec.action === 'ROLL' ? 'bg-purple-500' :
                                'bg-orange-500'
                              } text-white`}>
                                {rec.action}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getRiskBadge(rec.risk_level)}
                              {getRecommenderBadge(rec.recommended_by)}
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-gray-600">{rec.description}</p>

                          {/* Execution Details */}
                          {rec.execution && (
                            <div className="bg-gray-100 rounded-lg p-3 space-y-2">
                              <p className="text-sm font-semibold text-gray-700">Execution Details:</p>
                              
                              {/* Orders */}
                              {rec.execution.orders?.map((order, orderIdx) => (
                                <div key={orderIdx} className="flex items-center space-x-2 text-sm">
                                  <ArrowRight className="h-4 w-4 text-gray-400" />
                                  <span className={order.action.includes('BUY') ? 'text-green-600' : 'text-red-600'}>
                                    {order.action}
                                  </span>
                                  <span className="font-medium">
                                    {order.quantity}x {order.option_type} ${order.strike}
                                  </span>
                                  <span className="text-gray-500">
                                    exp {order.expiration}
                                  </span>
                                  {order.estimated_price && (
                                    <span className="text-gray-600">
                                      @ ~${order.estimated_price?.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              ))}

                              {/* Net Credit/Debit */}
                              {rec.execution.net_credit_debit && (
                                <div className="flex items-center space-x-2 pt-2 border-t">
                                  <DollarSign className="h-4 w-4 text-gray-500" />
                                  <span className={`font-bold ${
                                    rec.execution.net_credit_debit.includes('CREDIT') ? 'text-green-600' : 
                                    rec.execution.net_credit_debit.includes('DEBIT') ? 'text-red-600' : 
                                    'text-gray-600'
                                  }`}>
                                    {rec.execution.net_credit_debit}
                                  </span>
                                </div>
                              )}

                              {/* New Position */}
                              {rec.execution.new_position && (
                                <div className="text-sm text-gray-600 pt-1">
                                  <span className="font-medium">Result:</span> {rec.execution.new_position}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Profit Potential */}
                          {rec.profit_potential && (
                            <div className="flex items-center text-sm text-green-600">
                              <Target className="h-4 w-4 mr-1" />
                              <span>{rec.profit_potential}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* No Recommendations */}
            {!loading && recommendations.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Settings2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No recommendations available</p>
                <Button onClick={fetchRecommendations} className="mt-4">
                  Try Again
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with Execute Button - Always visible at bottom */}
        {recommendations.length > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
            <p className="text-sm text-gray-500">
              Select recommendations and execute through your brokerage
            </p>
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleExecuteSelected}
                disabled={selectedRecommendations.size === 0}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                data-testid="execute-selected-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Prepare Selected ({selectedRecommendations.size})
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PositionManager;
